import infrasController from "@/services/infrasService";
import infrasDetailController from "@/services/infrasDetailService";
import deviceTypeController from "@/services/deviceTypeService";
import parameterController from "@/services/parameterService";
import siteController from "@/services/siteService";
import divisionController from "@/services/divisionService";
import employeeController from "@/services/employeeService";
import deviceSpecificationController from "@/services/deviceSpecificationService";
import Swal from "sweetalert2";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";

const toUpper = (v) =>
  String(v ?? "")
    .trim()
    .toUpperCase();

export default {
  name: "InfrasFormView",

  components: { Multiselect },

  props: {
    mode: {
      type: String,
      required: true,
      validator: (v) => ["add", "edit", "detail"].includes(v),
    },
  },

  data() {
    return {
      isLoading: false,

      // master data
      deviceCategoryList: [],
      itemDeviceTypeList: [],
      siteList: [],
      divisionList: [],
      employeeList: [],
      userSelect: null,
      specificationList: [],
      documentTypeList: [],
      conditionList: [],

      // dokumen bertipe
      existingDocuments: [], // dari DB
      newDocuments: [], // { file, document_type }

      // detail view
      detail: {},

      // HEADER form
      form: {
        device_category: null,
        initial_location_device: "",
        final_location_device: "",
        purchase_date: "",
        received_date: "",
        handover_date: "",
        mst_site_id: null,
        mst_division_id: null,
        mst_employee_id: null,
        infra_user_name: "",
      },

      // DETAIL items
      detailItems: [this.buildEmptyItem()],
    };
  },

  computed: {
    pageTitle() {
      return {
        add: "Add Infrastruktur IT",
        edit: "Edit Infrastruktur IT",
        detail: "Detail Infrastruktur IT",
      }[this.mode];
    },

    submitLabel() {
      return this.mode === "add" ? "Simpan" : "Update";
    },

    isDetail() {
      return this.mode === "detail";
    },

    isEditOrDetail() {
      return this.mode === "edit" || this.mode === "detail";
    },
    // tipe dokumen "Bukti Penjualan / Pemusnahan" (null jika belum ada di param 13)
    retiredDocTypeValue() {
      return (
        this.documentTypeList.find((t) =>
          /pemusnahan|penjualan/i.test(t.label || ""),
        )?.value ?? null
      );
    },
    employeeOptions() {
      return (this.employeeList || []).map((e) => ({
        value: e.mst_employee_id,
        label: toUpper(e.mst_employee_name),
      }));
    },
  },

  watch: {
    "form.device_category"(newVal, oldVal) {
      if (oldVal !== null && oldVal !== undefined) {
        this.detailItems.forEach((item) => {
          item.device_type_id = null;
          item.specifications = [];
        });
      }
    },
  },

  methods: {
    // Nama User: pilih dari SSO (employee) atau ketik manual — sama seperti Asset
    onUserSelectChange(val) {
      if (val === null || val === undefined || val === "") {
        this.form.mst_employee_id = null;
        this.form.infra_user_name = "";
        return;
      }

      const id =
        typeof val === "object" ? val?.value ?? val?.mst_employee_id : val;
      const emp = this.employeeList.find(
        (e) => String(e.mst_employee_id) === String(id),
      );

      if (emp) {
        this.form.mst_employee_id = emp.mst_employee_id;
        this.form.infra_user_name = toUpper(emp.mst_employee_name);
        return;
      }

      // ketik manual -> simpan uppercase
      const name = typeof val === "object" ? val?.label || val?.value : val;
      this.form.mst_employee_id = null;
      this.form.infra_user_name = toUpper(name);
    },

    buildEmptyItem() {
      return {
        device_type_id: null,
        status: 1,
        asset_condition: null,
        detail_subscription: 0,
        detail_start_date_subscription: "",
        detail_expired_date_subscription: "",
        detail_subscription_contract_number: "",
        detail_tanggal_pembelian: "",
        asset_numbers: [{ detail_asset_number: "" }],
        specifications: [],
      };
    },
    goBack() {
      const from = this.$route.query.from;
      if (from) {
        this.$router.push(from);
      } else {
        this.$router.push("/asset/infrastructure-it");
      }
    },

    formatDate(date) {
      if (!date) return "-";
      return new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(new Date(date));
    },

    formatInputDate(date) {
      if (!date) return "";
      return new Date(date).toISOString().split("T")[0];
    },

    // FILTER DEVICE TYPE PER KATEGORI ASSET
    getFilteredDeviceTypes() {
      if (!this.form.device_category) return [];
      return this.itemDeviceTypeList.filter(
        (dt) =>
          Number(dt.device_category) === Number(this.form.device_category),
      );
    },

    //DYNAMIC SPEC FORM
    onDeviceTypeChange(index, deviceTypeId) {
      const item = this.detailItems[index];
      const id = deviceTypeId ?? item.device_type_id;

      if (!id) {
        item.specifications = [];
        return;
      }

      const rawFields = this.specificationList.filter(
        (s) => Number(s.device_type_id) === Number(id),
      );
      const fields = rawFields.filter((s) => Number(s.status) === 1);
      const oldValues = {};
      (item.specifications || []).forEach((s) => {
        oldValues[s.specification_id] = s.specification_value;
      });

      item.specifications = fields.map((f) => ({
        specification_id: f.specification_id,
        specification_name: f.specification_name,
        specification_value: oldValues[f.specification_id] || "",
      }));
    },

    // DETAIL ITEM METHODS
    addDetailItem() {
      this.detailItems.push(this.buildEmptyItem());
    },

    removeDetailItem(index) {
      if (this.detailItems.length === 1) return;
      this.detailItems.splice(index, 1);
    },

    //  ASSET NUMBER METHODS
    addAssetNumber(itemIndex) {
      this.detailItems[itemIndex].asset_numbers.push({
        detail_asset_number: "",
      });
    },

    removeAssetNumber(itemIndex, numIndex) {
      const numbers = this.detailItems[itemIndex].asset_numbers;
      if (numbers.length === 1) return;
      numbers.splice(numIndex, 1);
    },

    // ===== DOKUMEN BERTIPE (pola sama dengan AssetFormView) =====
    addNewDocument(event) {
      const files = Array.from(event.target.files || []).filter(
        (f) =>
          f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
      );
      const defaultType = this.documentTypeList[0]?.value ?? 5;
      files.forEach((file) => {
        if (file.size > 10 * 1024 * 1024) {
          Swal.fire("Warning", `${file.name}: ukuran maksimal 10MB`, "warning");
          return;
        }
        this.newDocuments.push({ file, document_type: defaultType });
      });
      event.target.value = "";
    },

    removeNewDocument(idx) {
      this.newDocuments.splice(idx, 1);
    },

    removeExistingDocument(idx) {
      this.existingDocuments.splice(idx, 1);
    },

    getFileName(key) {
      if (!key) return "";
      return key.split("/").pop().split("\\").pop();
    },

    async downloadFile(key) {
      if (!key) return;
      try {
        const res = await infrasController.getSignedUrl(key);
        if (res?.url) window.open(res.url, "_blank");
        else Swal.fire("Error", "Gagal mendapatkan link download", "error");
      } catch (err) {
        Swal.fire("Error", "Gagal mengunduh file", "error");
      }
    },

    // Bangun FormData untuk header (ada file upload)
    buildHeaderFormData() {
      const payload = new FormData();

      Object.keys(this.form).forEach((key) => {
        if (this.form[key] !== null && this.form[key] !== undefined) {
          payload.append(key, this.form[key]);
        }
      });

      // dokumen baru + tipenya (urutan sama)
      const docTypes = [];
      this.newDocuments.forEach((d) => {
        if (d.file instanceof File) {
          payload.append("documents", d.file);
          docTypes.push(d.document_type || 5);
        }
      });
      payload.append("document_types", JSON.stringify(docTypes));

      // dokumen lama yang dipertahankan
      const keepIds = this.existingDocuments
        .map((d) => d.document_file_id ?? d.asset_document_id)
        .filter(Boolean);
      payload.append("keep_document_ids", JSON.stringify(keepIds));

      return payload;
    },
    // ===== END FILE UPLOAD METHODS =====

    //  LOAD
    async loadMasterData() {
      try {
        const [
          category,
          deviceType,
          site,
          division,
          employee,
          specification,
          docType,
          condition,
        ] = await Promise.all([
          parameterController.getAll({ param_id: 7 }),
          deviceTypeController.getAll({ limit: 0, status: 1 }),
          siteController.getAll({ limit: 0 }),
          divisionController.getAll({ limit: 0 }),
          employeeController.getAll({ limit: 0 }),
          deviceSpecificationController.getAll({ limit: 0 }),
          parameterController.getAll({ param_id: 13, limit: 0, is_active: 1 }),
          parameterController.getAll({ param_id: 12, limit: 0, is_active: 1 }),
        ]);

        this.deviceCategoryList = category.data || [];
        this.itemDeviceTypeList = deviceType.data || [];
        this.siteList = site.data || [];
        this.divisionList = division.data || [];
        this.employeeList = (employee.data || []).sort((a, b) =>
          (a.mst_employee_name || "").localeCompare(b.mst_employee_name || ""),
        );
        this.specificationList = specification.data || [];
        this.documentTypeList = (docType.data || [])
          .map((p) => ({ value: Number(p.value), label: p.description }))
          .filter((t) => t.value !== 4); // 4 = gambar lisensi, bukan PDF
        this.conditionList = (condition.data || []).map((p) => ({
          value: Number(p.value),
          label: p.description,
        }));
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Gagal load master data",
        });
      }
    },

    async loadDetail() {
      const id = this.$route.params.id;

      if (!id || id === "undefined") {
        Swal.fire({ icon: "error", title: "Error", text: "ID tidak valid." });
        this.$router.push("/asset/infrastructure-it");
        return;
      }

      try {
        const [header, detailRes] = await Promise.all([
          infrasController.getById(id),
          infrasDetailController.getAll({ infrastructure_id: id, limit: 0 }),
        ]);

        if (!header) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Data tidak ditemukan.",
          });
          return;
        }

        if (this.mode === "detail") {
          const category = this.deviceCategoryList.find(
            (c) => c.value === header.device_category,
          );
          const site = this.siteList.find(
            (s) => s.mst_site_id === header.mst_site_id,
          );
          const division = this.divisionList.find(
            (d) => d.mst_division_id === header.mst_division_id,
          );

          this.detail = {
            ...header,
            device_category_name: category?.description || "-",
            mst_site_name: site?.mst_site_name || "-",
            mst_division_name: division?.mst_division_name || "-",
            infra_user_name: header.infra_user_name || "-",
            received_date: header.received_date ?? header.receive_date ?? null,
            documents: header.documents || [],
          };

          if (detailRes.data?.length) {
            this.detail.items = await Promise.all(
              detailRes.data.map(async (item) => {
                const detailId =
                  item.infrastructure_detail_id ??
                  item.infrastucture_detail_id ??
                  item.id;

                if (!detailId) return item;

                const full = await infrasDetailController.getById(detailId);
                if (!full) return item;

                const itemDeviceType = this.itemDeviceTypeList.find(
                  (d) => d.device_type_id === full.device_type_id,
                );

                return {
                  ...full,
                  device_type_name: itemDeviceType?.device_type_name || "-",
                };
              }),
            );
          } else {
            this.detail.items = [];
          }
        } else {
          //  MODE EDIT
          Object.assign(this.form, {
            device_category: header.device_category,
            initial_location_device: header.initial_location_device,
            final_location_device: header.final_location_device,
            purchase_date: this.formatInputDate(header.purchase_date),
            received_date: this.formatInputDate(
              header.received_date ?? header.receive_date,
            ),
            handover_date: this.formatInputDate(header.handover_date),
            mst_site_id: header.mst_site_id,
            mst_division_id: header.mst_division_id,
            mst_employee_id: header.mst_employee_id ?? null,
            infra_user_name: header.infra_user_name || "",
          });
          // Nama User object mode — sama fix Asset PC/Laptop
          const empName = toUpper(
            header.infra_user_name || header.mst_employee_name || "",
          );
          this.form.infra_user_name = empName || "";
          this.form.mst_employee_id = header.mst_employee_id ?? null;

          if (header.mst_employee_id) {
            const empId = Number(header.mst_employee_id);
            const exists = (this.employeeList || []).some(
              (e) => Number(e.mst_employee_id) === empId,
            );
            if (!exists && empName) {
              this.employeeList = [
                {
                  mst_employee_id: empId,
                  mst_employee_name: empName,
                  mst_employee_nik: header.mst_employee_nik || "",
                },
                ...this.employeeList,
              ];
            }
            this.userSelect = {
              value: empId,
              label: empName || String(empId),
              mst_employee_id: empId,
              mst_employee_name: empName,
            };
          } else if (empName) {
            this.userSelect = { value: empName, label: empName };
          } else {
            this.userSelect = null;
          }
          this.existingDocuments = header.documents || [];
          this.newDocuments = [];

          if (detailRes.data?.length) {
            const itemsWithChildren = await Promise.all(
              detailRes.data.map(async (item) => {
                const detailId =
                  item.infrastructure_detail_id ??
                  item.infrastucture_detail_id ??
                  item.id;

                if (!detailId) return null;

                const full = await infrasDetailController.getById(detailId);
                if (!full) return null;

                return {
                  infrastructure_detail_id: full.infrastructure_detail_id,
                  device_type_id: full.device_type_id,
                  status: full.status ?? 1,
                  asset_condition:
                    full.asset_condition != null
                      ? Number(full.asset_condition)
                      : null,
                  detail_subscription: full.detail_subscription,
                  detail_start_date_subscription: this.formatInputDate(
                    full.detail_start_date_subscription,
                  ),
                  detail_expired_date_subscription: this.formatInputDate(
                    full.detail_expired_date_subscription,
                  ),
                  detail_subscription_contract_number:
                    full.detail_subscription_contract_number,
                  detail_tanggal_pembelian: this.formatInputDate(
                    full.detail_tanggal_pembelian,
                  ),
                  asset_numbers: full.asset_numbers?.length
                    ? full.asset_numbers
                    : [{ detail_asset_number: "" }],
                  specifications: full.specifications || [],
                };
              }),
            );

            this.detailItems = itemsWithChildren.filter(Boolean);
          }
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text: "Gagal memuat data infrastruktur",
        });
      }
    },

    // Susun payload detail item, filter asset_numbers yg kosong sebelum dikirim
    buildDetailPayload(item) {
      return {
        ...item,
        asset_numbers: item.asset_numbers.filter((n) =>
          n.detail_asset_number?.trim(),
        ),
      };
    },

    //  SAVE
    async saveData() {
      await this.$nextTick();

      // validasi header — di luar loop
      if (!this.form.device_category) {
        Swal.fire({
          icon: "warning",
          title: "Warning",
          text: "Jenis Asset wajib diisi",
        });
        return;
      }
      if (!this.form.mst_site_id) {
        Swal.fire({
          icon: "warning",
          title: "Warning",
          text: "Site wajib diisi",
        });
        return;
      }

      // validasi detail items (asset number boleh kosong)
      for (let i = 0; i < this.detailItems.length; i++) {
        const item = this.detailItems[i];

        if (!item.device_type_id) {
          Swal.fire({
            icon: "warning",
            title: "Warning",
            text: `Item #${i + 1}: jenis perangkat wajib diisi`,
          });
          return;
        }
      }

      // item Retired wajib punya bukti penjualan / pemusnahan
      const retiredType = this.retiredDocTypeValue;
      if (
        retiredType !== null &&
        this.detailItems.some((it) => Number(it.status) === 2)
      ) {
        const hasProof =
          this.existingDocuments.some(
            (d) => Number(d.document_type) === retiredType,
          ) ||
          this.newDocuments.some(
            (d) => Number(d.document_type) === retiredType,
          );
        if (!hasProof) {
          Swal.fire({
            icon: "warning",
            title: "Warning",
            text: "Ada item berstatus Retired. Upload dokumen Bukti Penjualan / Pemusnahan terlebih dahulu.",
          });
          return;
        }
      }

      this.isLoading = true;
      Swal.fire({
        title: "Menyimpan Dokumen...",
        html: "Mohon tunggu, jangan tutup atau refresh halaman ini.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      let createdInfraId = null;

      try {
        const id = this.$route.params.id;
        const headerPayload = this.buildHeaderFormData();

        if (this.mode === "add") {
          const headerRes = await infrasController.create(headerPayload);

          const infraId = headerRes?.id;
          if (!infraId) {
            Swal.fire({
              icon: "error",
              title: "Gagal",
              text: "Gagal mendapatkan ID infrastruktur",
            });
            return;
          }

          createdInfraId = infraId;
          for (const item of this.detailItems) {
            const detailPayload = {
              ...this.buildDetailPayload(item),
              infrastructure_id: infraId,
            };
            await infrasDetailController.create(detailPayload);
          }
        } else {
          await infrasController.update(id, headerPayload);

          for (const item of this.detailItems) {
            if (item.infrastructure_detail_id) {
              await infrasDetailController.update(
                item.infrastructure_detail_id,
                this.buildDetailPayload(item),
              );
            } else {
              await infrasDetailController.create({
                ...this.buildDetailPayload(item),
                infrastructure_id: id,
              });
            }
          }
        }

        Swal.fire({
          icon: "success",
          title: "Berhasil",
          text:
            this.mode === "add"
              ? "Data berhasil disimpan"
              : "Data berhasil diupdate",
        });

        this.goBack();
      } catch (error) {
        console.error("saveData error:", error);
        if (this.mode === "add" && createdInfraId) {
          try {
            await infrasController.remove(createdInfraId);
          } catch (rollbackError) {
            console.error(
              "Gagal rollback header infrastruktur:",
              rollbackError,
            );
          }
        }

        Swal.fire({
          icon: "error",
          title: "Gagal",
          text: error.response?.data?.message || "Terjadi kesalahan",
        });
      } finally {
        this.isLoading = false;
      }
    },
  },

  async mounted() {
    await this.loadMasterData();
    if (this.isEditOrDetail) await this.loadDetail();
    else this.detailItems.forEach((_, i) => this.onDeviceTypeChange(i));
  },
};
